import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@lb-classroom/feature-auth';
import CurrentUserApiUtils from '@utils/api/currentUser';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type { DeactivateConfirmationModalContainerProps } from './deactivate-confirmation-modal.types';

export const useDeactivateConfirmationModalService = (
  props: DeactivateConfirmationModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage } = props;
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();
  const { addFlash } = useFlashMessage();

  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = useCallback(() => {
    setChecked((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!checked) {
        addModalMessage(t('modals.deactivateConfirmation.checkNeeded'), {
          autoDisappear: true,
          type: 'error',
        });
        return;
      }

      if (!currentUser?.id) {
        addModalMessage('User not found', {
          autoDisappear: false,
          type: 'error',
        });
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        await CurrentUserApiUtils.deactivate(currentUser.id);

        addFlash('Account successfully deactivated', {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        });
        hideModal();
        // The page will likely redirect after deactivation
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        addModalMessage(errorMessage, {
          autoDisappear: false,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [
      checked,
      currentUser?.id,
      t,
      addModalMessage,
      clearModalMessage,
      addFlash,
      hideModal,
    ],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    checked,
    isSubmitting,
    message: props.message,
    messageType: props.messageType,
    onCheckboxChange: handleCheckboxChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
